import type { AxiosResponse } from 'axios';
import * as _ from 'lodash';

import type { CheckEnitityAliasRequest } from '@/models/CheckEnitityAliasRequest';
import type { AuditTrailFilters } from '@/pages/AuditTrail/AuditTrail';

import { OrderBy, SearchOutPutType } from '../../enums';
import type {
  Channel,
  ChannelMetaData,
  ClipUsers,
  CreateAssetResponse,
  Ingest,
  Item,
  Pagination,
  SearchRequest,
} from '../../models';
import type { BatchDeleteResponse } from '../../models/batchDeleteResponse';
import type { ChannelSubtype } from '../../models/channel';
import type { ClientSettings } from '../../models/client-settings';
import type { ClipDurationResponse } from '../../models/fuelPortalToolsEndpointModels';
import type { FuelUserAccess, FuelUserPermissions } from '../../models/FuelUserAccess';
import type { AssetAuditTrail } from '../../models/item';
import type { OU } from '../../models/Ou';
import type { Replay } from '../../models/Replay';
import type { Tag } from '../../models/Tag';
import type { AssetFilterTypeOptions } from '../../store/forms/Asset-Editor/AssetFilterTypeOptions';
import clientUtils from '../../utils/clientUtils';
import { baseAxios, fuelPortalAxios } from '../interceptor';
import { LocalStorageService } from '../local-storage';

enum FuelEntityType {
  Channel = 'Channel',
  ContentPool = 'ContentPool',
  Clip = 'Clip',
  LiveProgram = 'LiveProgram',
  Transition = 'Transition',
  Ou = 'Ou',
  ClipGroup = 'ClipGroup',
  EPGData = 'EPGData',
  AdTag = 'AdTag',
  Input = 'Input',
  Site = 'Site',
  LiveEvent = 'LiveEvent',
  LiveGuide = 'LiveGuide',
  Destination = 'Destination',
}

// const source = baseAxios.CancelToken.source()
const FuelPortalService = {
  // log: new Log(),
  getClipByIdsArray: [],

  async post(route, data: any) {
    try {
      const fullRoute = `${route}`;
      const res = await fuelPortalAxios.post(fullRoute, data);
      return res.data;
    } catch (err) {
      console.error('err', err);
      // store.commit('alert/set', {
      //   message: clientUtils.errorResponseHandler(err, 'FuelPortalService.get'),
      //   type: 'error',
      //   position: 'n',
      // });
      throw err;
    }
  },

  // Generic method for any GET request
  /// <param>route</param> relative route without a beginning slash. eg. 'data/clip/list'
  /// <param>queryParams</param> is a Dictionary<string, object> of all query params with values already url encoded.
  /// e.g. { 'byChannel': b610c1b0-c37f-4a47-907d-ef22b7007f78, 'limit': 100 }
  async get(route, queryParams: any = null) {
    // https://fueltools-dev01-v1.mediapipe.com/data/clip/list?byChannel=b610c1b0-c37f-4a47-907d-ef22b7007f78
    // const itemId = activeSubClip.item.id;
    // const data = { ouId: store.state.user.profile.ou_id };
    // const url = `tools/publishsubclip/${itemId}`;
    // const fullUrl = fuelPortalAxios.defaults.baseURL + url;
    let fullRoute = `${route}`;
    if (queryParams != null) {
      const qs = Object.keys(queryParams)
        .map((key) => `${key}=${queryParams[key]}`)
        .join('&');
      fullRoute += `?${qs}`;
    }
    try {
      const res = await fuelPortalAxios.get(fullRoute).catch((err) => {
        return err;
      });
      return res.data;
    } catch (err) {
      console.error('err', err);
      // store.commit('alert/set', {
      //   message: clientUtils.errorResponseHandler(err, 'FuelPortalService.get'),
      //   type: 'error',
      //   position: 'n',
      // });
      throw err;
    }
  },

  showError(err) {
    let errorMessage =
      'Request was not allowed! Please validate the inputs and configurations are correct. Contact support for more details.';
    if (err && err.response && err.response.data) {
      errorMessage = err.response.data;
    }
    if (err && err.data && err.data.Message && !err.data.InnerException) {
      // console.log('!InnerException', err.data.InnerException);
      errorMessage = err.data.Message;
    }
    if (err && err.data && err.data.Message && err.data.InnerException && err.data.InnerException.Message) {
      // console.log('InnerException', err.data.InnerException);
      errorMessage = err.data.InnerException.Message;
    }
    if (err && err.data && !err.data.Message) {
      errorMessage = err.data;
    }
    throw new Error(errorMessage);
  },
  async deleteChannel(channelId: string): Promise<Channel> {
    if (!_.isString(channelId)) {
      console.error('deleteChannel method expects channel id as a string you provided...', { channelId });
      return null;
    }
    const url = `data/channel/${channelId}`;
    try {
      const response = await fuelPortalAxios.delete<Channel>(url);
      return response.data;
    } catch (err) {
      return err;
    }
  },

  async getClipDuration(clip: Item, data: ChannelMetaData): Promise<ClipDurationResponse> {
    // console.log('getClipDuration', { clip, data });
    const url = `tools/clipDuration/${clip.id}`;
    if (data?.adConfiguration) {
      const adConfigCopy = _.cloneDeep(data.adConfiguration);
      // special transformation to reduce adSponsors form option object to its string id
      if (adConfigCopy.adBreakType != null && _.isObject(adConfigCopy.adBreakType)) {
        adConfigCopy.adBreakType = adConfigCopy.adBreakType.id;
      }
      data.adConfiguration = adConfigCopy;
    }
    try {
      const response = await fuelPortalAxios.post<ClipDurationResponse>(url, data);
      return response.data;
    } catch (err) {
      return err;
    }
  },

  async getClientSettings(): Promise<ClientSettings> {
    const url = `tools/clientsettings`;
    try {
      const response = await fuelPortalAxios.get<ClientSettings>(url);
      return response.data;
    } catch (err) {
      return err;
    }
  },

  /**
   *
   * @returns Returns the accessible OUs, else null
   */
  async getUserPermissions(ouId: string): Promise<FuelUserPermissions> {
    // console.log('getUserPermissions', ouId);
    const url = `auth/userpermissions/${ouId}`;
    const source = baseAxios.CancelToken.source();
    try {
      const response = await fuelPortalAxios.get<any>(url, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (err) {
      console.error(err);
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', err);
      }
      return err;
    }
  },

  /**
   *
   * @returns Returns the accessible OUs, else null
   */
  async getUserAccess(): Promise<FuelUserAccess> {
    // console.log('getUserAccess');
    const url = `auth/useraccess`;
    const source = baseAxios.CancelToken.source();
    try {
      const response = await fuelPortalAxios.get<any>(url, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (err) {
      console.error(err);
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', err);
      }
      return err;
    }
  },

  async getOuData(ouId: string, fullInfo: boolean, tagsSearchText?: string): Promise<OU> {
    const buildUrl = () => {
      const path = `data/ou/${ouId}`;
      if (tagsSearchText !== undefined) {
        const data = {
          filterTags: tagsSearchText,
        };
        return `${path}?${clientUtils.convertToQueryString(data)}`;
      }
      if (fullInfo) {
        const extra = `?fullInfo=true&includeDeletedCategories=false`;
        return `${path}${extra}`;
      }
      return path;
    };

    const url = buildUrl();
    const source = baseAxios.CancelToken.source();
    const fullUrl = fuelPortalAxios.defaults.baseURL + url;
    const method = 'get';
    try {
      const res = await fuelPortalAxios[method](url, {
        cancelToken: source.token,
      });
      return res.data;
    } catch (err) {
      console.error(err);
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', err);
      }
      return err;
    }
  },

  // actually using the "bulk data" /data/ou endpoint that contains all sorts of stuff, but the scope of this particular function is limited
  //   to just searching for tags...
  async searchTags(ouId: string, searchText: string): Promise<Array<Tag>> {
    const ouData = await this.getOuData(ouId, false, searchText);
    return ouData.tags;
  },
  /**
   * Calls the FuelPortal data/clips/list route to get List of items
   *
   * @returns {Promise<any>}
   */
  async getList(entityType: string, queryParams: Object, additionalQueryString: string | null = null): Promise<any> {
    // endpoint url looks like this
    // 'http://localhost:5051/data/clip/list?page=0&size=100&ouId=fe5d4764-87e0-49d1-a171-a4e56ec36d06&keyword=aalt&outputType=simple&sort=createdDate&order=desc&showExpiredAssets=false&tags=aalt-2020-11-17'
    let url = `data/${entityType}/list`;
    const source = baseAxios.CancelToken.source();

    if (queryParams) {
      url += `?${clientUtils.convertToQueryString(queryParams)}`;
    }
    if (additionalQueryString) {
      url += additionalQueryString;
    }

    // if (queryParams) {
    //   url = `${url}?${queryParams}`;
    // }
    // const fullUrl = fuelPortalAxios.defaults.baseURL + url;
    try {
      const res = await fuelPortalAxios.get(url, {
        cancelToken: source.token,
      });
      return res.data;
    } catch (err) {
      console.error('Unable to get list data err', { err });
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', { err });
      }
      return err;
    }
  },
  /**
   * search via fuel Portal
   * Get all contents for organizational unit based on search keyword
   *
   * @param {string} keyword - the text to search for (eg. 'Star Wars')
   * @param {number} page - the current page (eg. 0, 1, or 2)
   * @param {number} pageSize - number of records to show per page (eg. 5, 10, 15, 20, or 25)
   * @param {string[]} categoryNames - Optional: a list of the categoryNames to find.
   * @param {string[]} tags - Optional: a list of the tags to find.
   * @param {string} sortBy
   * @param {string} orderBy
   * @returns a response object with data and pagination attrubutes.
   *
   */
  async searchAsset(
    keyword: string,
    ouId: string,
    page: number,
    pageSize: number = 100,
    categoryNames: string[] = [],
    tags: string[] = [],
    sortBy: string = 'createdDate',
    orderBy: string = 'desc',
    showExpiredAssets: boolean = true,
    entityType: string = 'clip',
    schema: string = 'Video',
    showRecentlyIngestedAssets: boolean = false,
    assetFilterTypes: AssetFilterTypeOptions[] | null = null,
    excludeAssetFilterTypes: AssetFilterTypeOptions[] | null = null,
    durationFrom: number | null = null,
    durationTo: number | null = null,
    publicationDateFrom: string | null = null,
    publicationDateTo: string | null = null,
    ingestDateFrom: string | null = null,
    ingestDateTo: string | null = null,
    expiredDateFrom: string | null = null,
    expiredDateTo: string | null = null,
    includeAudioTranscripts: boolean = false,
    extensions: string[] | null = null,
    excludeCategories: string[] | null = null,
    excludeTags: string[] | null = null
  ): Promise<any> {
    // console.log('searchAsset', { keyword, ouId, page, pageSize });
    try {
      if (!keyword) {
        keyword = '*';
      }
      const data = <SearchRequest>{
        page,
        keyword,
        entityType,
        showRecentlyIngestedAssets,
        showExpiredAssets,
        includeAudioTranscripts,
        durationFrom,
        durationTo,
        publicationDateFrom,
        publicationDateTo,
        ingestDateFrom,
        ingestDateTo,
        expiredDateFrom,
        expiredDateTo,
        size: pageSize,
        ouId,
        outputType: SearchOutPutType.SIMPLE,
        sort: sortBy,
        order: orderBy,
      };

      if (schema && schema != '') {
        data.schema = schema;
      }
      let additionalQueryString = '';

      if (assetFilterTypes && assetFilterTypes.length > 0) {
        for (let i = 0; i < assetFilterTypes.length; i++) {
          if (assetFilterTypes[i]) {
            additionalQueryString += `&assetFilterTypes=${encodeURIComponent(assetFilterTypes[i].id)}`;
          }
        }
      }

      if (excludeAssetFilterTypes && excludeAssetFilterTypes.length > 0) {
        for (let i = 0; i < excludeAssetFilterTypes.length; i++) {
          if (excludeAssetFilterTypes[i]) {
            additionalQueryString += `&excludeAssetFilterTypes=${encodeURIComponent(excludeAssetFilterTypes[i].id)}`;
          }
        }
      }

      if (categoryNames && categoryNames.length > 0) {
        for (let i = 0; i < categoryNames.length; i++) {
          if (categoryNames[i]) {
            additionalQueryString += `&filter_categories=${encodeURIComponent(categoryNames[i])}`;
          }
        }
      }

      if (tags && tags.length > 0) {
        for (let i = 0; i < tags.length; i++) {
          if (tags[i]) {
            additionalQueryString += `&filter_tags=${encodeURIComponent(tags[i])}`;
          }
        }
      }
      if (excludeCategories && excludeCategories.length > 0) {
        for (let i = 0; i < excludeCategories.length; i++) {
          if (excludeCategories[i]) {
            additionalQueryString += `&excluded_categories=${encodeURIComponent(excludeCategories[i])}`;
          }
        }
      }
      if (excludeTags && excludeTags.length > 0) {
        for (let i = 0; i < excludeTags.length; i++) {
          if (excludeTags[i]) {
            additionalQueryString += `&excluded_tags=${encodeURIComponent(excludeTags[i])}`;
          }
        }
      }

      if (extensions && extensions.length > 0) {
        for (let i = 0; i < extensions.length; i++) {
          if (extensions[i] != null) {
            additionalQueryString += `&ext=${encodeURIComponent(extensions[i])}`;
          }
        }
      }

      const res = await FuelPortalService.getList(FuelEntityType.Clip, data, additionalQueryString);
      return res;
    } catch (err) {
      // store.commit('alert/set', {
      //   message: clientUtils.errorResponseHandler(err, 'search'),
      //   type: 'error',
      //   position: 'n',
      // });
      // throw new Error();
      console.error('Unable to get asset list due to', err);
    }
  },

  /**
   * Saves and returns the updated object from the backend.
   * @param queryParams The fully formed query params as a string eg. "?queryParam=value&queryParam=value2&otherParam=value3"
   */
  async upsertEntity<T>(
    entityType: FuelEntityType,
    obj: T,
    queryParams: string | null = null,
    goToOopsPage: boolean = true
  ): Promise<T | null> {
    let url = `data/${entityType}`;
    if (queryParams) {
      url += queryParams;
    }
    // console.log('url', url);
    const data = obj;
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios
        .post(url, data, {
          cancelToken: source.token,
        })
        .catch((err) => {
          // console.log('err', { err });
          return Promise.reject(err);
        });
      // console.log('res', { res });
      if (res === null || res.data === null) {
        FuelPortalService.showError(null);
      }
      return res.data;
    } catch (err) {
      console.error('Error caught in upsertEntity.', url, { err });
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', err);
      }
      if (goToOopsPage) {
        FuelPortalService.showError(err);
      }
      if (err.code === 'ERR_BAD_RESPONSE') {
        return { IS_ERROR: true, data: err?.response?.data?.InnerException?.Message };
      }
      return null;
      // return Promise.reject(err);
    }
  },

  async deleteEntityById<T>(entityType: FuelEntityType, id: string | undefined, queryParams: any = null): Promise<T | null> {
    let url = `data/${entityType}/${id}`;
    if (queryParams) {
      url = `${url}?${clientUtils.convertToQueryString(queryParams)}`;
    }
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.delete(url, {
        cancelToken: source.token,
      });
      return res.data;
    } catch (err) {
      console.error('Unable to get entity by id ', err);
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', err);
      }
      FuelPortalService.showError(err);
      return null;
    }
  },

  // eg. GET https://fueltools-dev01-v1.mediapipe.com/tools/clip-projects/b56b4f7b-dd44-44d9-85cd-8635566c8568
  // async getClipProject<T>(entityType: FuelEntityType, id: string | undefined, queryParams: any = null): Promise<T> {
  //   let url = `tools/clip-projects/${id}`;
  //   if (queryParams) {
  //     url = `${url}?${clientUtils.convertToQueryString(queryParams)}`;
  //   }
  //   try {
  //     const source = baseAxios.CancelToken.source();
  //     const res = await fuelPortalAxios.get(url, {
  //       cancelToken: source.token,
  //     });
  //     return res.data;
  //   } catch (err) {
  //     console.error('Unable to get clip project by id ', err);
  //     if (!baseAxios.isCancel(err)) {
  //       console.error('not successfully aborted', err);
  //     }
  //     return err;
  //   }
  // },

  async getEntityById<T>(entityType: FuelEntityType, id: string | undefined, queryParams: any = null): Promise<T> {
    let url = `data/${entityType}/${id}`;
    if (queryParams) {
      url = `${url}?${clientUtils.convertToQueryString(queryParams)}`;
    }
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.get(url, {
        cancelToken: source.token,
      });
      return res.data;
    } catch (err) {
      console.error('Unable to get entity by id ', err);
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', err);
      }
      return err;
    }
  },

  async batchGet<T>(entityType: FuelEntityType, ids: string[] | undefined, queryParams: any = null): Promise<T> {
    let url = `data/${entityType}/batchget`;
    if (queryParams) {
      url = `${url}?${clientUtils.convertToQueryString(queryParams)}`;
    }
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.post(url, ids, {
        cancelToken: source.token,
      });
      return res.data;
    } catch (err) {
      console.error('Unable to batchGet entity by ids ', err);
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', err);
      }
      return err;
    }
  },
  async getAuditTrail(ouId: string, page: number, pageSize: number): Promise<any> {
    const url = `tools/useractivity?ouId=${ouId}&page=${page}&pageSize=${pageSize}`;
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.get(url, {
        cancelToken: source.token,
      });
      return res.data;
    } catch (err) {
      throw new Error();
    }
  },

  /** new audit trail / analytics query */
  async getAnalyticsQuery(ouId: string, filters: AuditTrailFilters): Promise<any> {
    const url = 'analytics/query';
    try {
      const res = await fuelPortalAxios.get(url, {
        params: {
          ...filters,
          ouIds: ouId,
        },
      });
      return res.data;
    } catch (err) {
      console.error('Unable to get analytics query data', err);
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', err);
      }
      throw new Error();
    }
  },

  /**
   * Return channel list with / without deleted channels for given ouId
   *
   * @param {any} showDeleted
   * @param {number} size
   * @param {number} page
   * @param {string} sortBy
   * @param {string} orderBy
   * @returns {Promise<any>}
   */
  async getAllChannels(
    searchKeyword: string,
    ouId: string,
    showDeleted: any,
    size: number,
    page: number,
    sortBy: string = '',
    orderBy: string = '',
    favoriteChannelIds: string = '',
    returnAsTree: boolean = false,
    includeSubtypes: ChannelSubtype[] = [],
    excludeSubtypes: ChannelSubtype[] = [],
    byWorkflow: boolean = false,
    workflowGroup: string = '',
    ouIds: string[] = [],
    enabled: boolean = false
  ): Promise<any> {
    const queryParams = {
      searchKeyword,
      ouId,
      showDeleted,
      size,
      page,
      sortBy,
      orderBy,
      favoriteChannelIds,
      returnAsTree,
      enabled,
    };
    let additionalQueryString = '';

    if (byWorkflow === true) {
      queryParams.byWorkflow = true;
    }

    if (!clientUtils.isNullOrWhitespace(workflowGroup)) {
      queryParams.workflowGroup = workflowGroup;
    }

    if (ouIds != null && ouIds.length > 0) {
      for (let i = 0; i < ouIds.length; i += 1) {
        const myOuId = ouIds[i];
        if (!clientUtils.isNullOrWhitespace(myOuId) && ouId !== myOuId) {
          additionalQueryString += `&ouId=${myOuId}`;
        }
      }
    }

    if (includeSubtypes && includeSubtypes.length > 0) {
      for (let i = 0; i < includeSubtypes.length; i++) {
        if (includeSubtypes[i]) {
          additionalQueryString += `&includeSubtype=${includeSubtypes[i]}`;
        }
      }
    }

    if (excludeSubtypes && excludeSubtypes.length > 0) {
      for (let i = 0; i < excludeSubtypes.length; i++) {
        if (excludeSubtypes[i]) {
          additionalQueryString += `&excludeSubtype=${excludeSubtypes[i]}`;
        }
      }
    }

    // const queryParams = `searchKeyword=${searchKeyword}&ouId=${ouId}&showDeleted=${showDeleted}&size=${size}&page=${page}&sortBy=${sortBy}&orderBy=${orderBy}`;
    return FuelPortalService.getList(FuelEntityType.Channel, queryParams, additionalQueryString);
  },

  // https://fueltools-dev01-v1.mediapipe.com/data/channel/list?ouId=c8801903-18a3-4564-afdc-b8ce901b1038&searchByLiveProgramId=e0cbaeb7-3de1-4ae7-9ad1-60ea76b816e2&showDeleted=false&size=100&page=1&sortBy=name&orderBy=asc
  async getChannelsByLiveProgramId(
    ouId: string,
    searchByLiveProgramId: string,
    showDeleted: boolean = false,
    size: number = 100,
    page: number = 0, // #2822 aalt this needs to be 0 or we will skip the first 100 records
    sortBy: string = 'name',
    orderBy: string = 'asc'
  ): Promise<any> {
    try {
      const p = {
        ouId,
        searchByLiveProgramId,
        showDeleted,
        size,
        page,
        sortBy,
        orderBy,
      };
      // const queryParams = clientUtils.convertToQueryString(p);
      return await FuelPortalService.getList('channel', p);
    } catch (err) {
      // rootStore.commit('alert/set', { message: clientUtils.errorResponseHandler(err, "getAllChannels"), type:"error", position:"n" });
      throw new Error();
    }
  },

  /**
   * Return transition template list for given ouId
   *
   * @param {PaginationRequest} pagination
   * @param {boolean} enabledOnly If true, returns only enabled transition templates.  Otherwise, it returns all transition templates.
   * @returns {Promise<any>}
   */
  async getAllTransitionTemplates(
    ouId: string,
    pagination: Pagination,
    enabledOnly: boolean = false,
    searchKeyWord?: string
  ): Promise<any> {
    const size = pagination.rowsPerPage;
    const page = pagination.page > 0 ? pagination.page - 1 : pagination.page;
    const { sortBy } = pagination;
    const orderBy = pagination.descending ? OrderBy.DESC : OrderBy.ASC;
    const queryParams: any = { ouId, size, page, sortBy, orderBy };
    // let queryParams = `ouId=${ouId}&size=${size}&page=${page}&sortBy=${sortBy}&orderBy=${orderBy}`;
    if (enabledOnly) {
      queryParams.enabledOnly = enabledOnly;
      // queryParams += `&enabledOnly=${enabledOnly}`;
    }
    if (searchKeyWord) {
      queryParams.keyword = searchKeyWord;
      // queryParams += `&keyword=${searchKeyWord}`;
    }
    return FuelPortalService.getList(FuelEntityType.Transition, queryParams);
  },
  async createOU(ou: OU): Promise<OU> {
    const promise = this.upsertEntity(FuelEntityType.Ou, ou) as Promise<OU>;
    return promise;
  },
  async updateOU(ou: OU): Promise<OU> {
    const promise = this.upsertEntity(FuelEntityType.Ou, ou) as Promise<OU>;
    return promise;
  },
  // same as the update version, but favoring explicit naming...
  async createAsset(asset: Item): Promise<Item> {
    const promise = this.upsertEntity(FuelEntityType.Clip, asset) as Promise<Item>;
    return promise;
  },

  // same as the create version, favoring explicit naming...
  async updateAsset(asset: Item, restrictedFields: string[] = []): Promise<Item> {
    let queryParams = '';
    // aalt 2023-10-18 #448947 Validate Feed-Ingested Ad Markers On Ingest -- send in the right flags
    if (asset.uiSaveFlags) {
      if (asset.uiSaveFlags.hasTimeStampMarkersChange) {
        if (restrictedFields == null) {
          restrictedFields = [];
        }
        if (!restrictedFields.includes('Metadata.TimeStampMarkers')) {
          restrictedFields.push('Metadata.TimeStampMarkers');
        }
      }
    }
    if (restrictedFields.length > 0) {
      // aalt 2023-06-16 old way
      // queryParams = '?allowWriteRestrictedFields=true';
      queryParams = `?allowWriteRestrictedField=${restrictedFields.join('&allowWriteRestrictedField=')}`;
    }
    const promise = this.upsertEntity(FuelEntityType.Clip, asset, queryParams) as Promise<Item>;
    return promise;
  },
  /**
   * Get upload url
   *
   * @returns {Promise<any>}
   */
  async getUploadUrl(ouId: string, fileName?: string, type = 'Temporary'): Promise<any> {
    const buildUrl = () => {
      const path = `data/ou/${ouId}/upload/${type}?filename=${fileName}`;
      return path;
    };
    try {
      const url = buildUrl();
      const res = await fuelPortalAxios.get(url);
      return res.data;
    } catch (err) {
      // store.commit('alert/set', {
      //   message: clientUtils.errorResponseHandler(err, 'getUploadUrl'),
      //   type: 'error',
      //   position: 'n',
      // });
      throw new Error();
    }
  },
  async getSiteUploadUrl(ouId: string, fileName?: string, type = 'Temporary'): Promise<any> {
    const buildUrl = () => {
      const path = `data/site/${ouId}/upload/${type}?filename=${fileName}`;
      return path;
    };
    try {
      const url = buildUrl();
      const res = await fuelPortalAxios.get(url);
      return res.data;
    } catch (err) {
      // store.commit('alert/set', {
      //   message: clientUtils.errorResponseHandler(err, 'getUploadUrl'),
      //   type: 'error',
      //   position: 'n',
      // });
      throw new Error();
    }
  },

  /**
   * Get Clip Download link from FuelPortal
   * eg. https://pyw4ye3nli.execute-api.us-east-1.amazonaws.com/Prod/data/Clip/09c7e709-3ffc-4914-ab06-47b1bad52b6b/download
   *
   * @returns {Promise<any>}
   */
  async getDownloadLink(id: string, fileName: string, videoProxy?: boolean): Promise<any> {
    let result = null;
    let url = `data/clip/${id}/download?filename=${fileName}`;
    const fullUrl = fuelPortalAxios.defaults.baseURL + url;
    if (videoProxy) {
      url = `${url}&videoProxyUrl=true`;
    }
    const method = 'get';
    try {
      const res = await fuelPortalAxios[method](url);
      result = res.data;
    } catch (ex) {
      if (ex == null) {
        ex = 'Some error occured downloading asset.';
      }
      console.error(ex, { fullUrl });
      // store.commit('alert/set', {
      //   message: clientUtils.errorResponseHandler(ex, 'getDownloadLink'),
      //   type: 'error',
      //   position: 'n',
      // });
    }
    return result;
  },
  /**
   * Create ingest for given metadata
   *
   * @returns {Promise<any>}
   */
  async createIngest(ingests: Ingest[], useFuelPortalIngest: boolean = true): Promise<any> {
    try {
      let res;

      if (useFuelPortalIngest) {
        res = [];
        ingests.forEach(async (ingest) => {
          if (ingest && ingest.item && ingest.item.id) {
            res.push(await fuelPortalAxios.post(`${this.toolsUrl}/startingest/${ingest.item.id}`, ingest.item));
          }
        });
      }
      return res;
    } catch (err) {
      // store.commit('alert/set', {
      //   message: clientUtils.errorResponseHandler(err, 'createIngest'),
      //   type: 'error',
      //   position: 'n',
      // });
      FuelPortalService.showError(err);
      return null;
    }
  },

  /**
   * Returns the dto of the newly created object duplicated from the id and type provided.
   * @param entityType
   * @param id
   * @param queryParams
   * @returns
   */
  async duplicateEntity<T>(entityType: FuelEntityType, id: string | undefined, queryParams: any = null): Promise<T | null> {
    let url = `data/${entityType}/${id}/duplicate`;
    if (queryParams) {
      url = `${url}?${clientUtils.convertToQueryString(queryParams)}`;
    }
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.get(url, {
        cancelToken: source.token,
      });
      return res.data;
    } catch (err) {
      console.error('Unable to get entity by id ', err);
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', err);
      }
      FuelPortalService.showError(err);
      return null;
    }
  },

  /**
   * Call startingest with parameters
   * eg. POST https://fueltools-dev01-v1.mediapipe.com/tools/startingest/{clipId}
   * @returns {Promise<any>}
   */
  async startIngestByClipId(clipId: string): Promise<any> {
    if (clientUtils.isNullOrWhitespace(clipId)) {
      return null;
    }
    const url = `tools/startingest/${clipId}`;
    try {
      const res = await fuelPortalAxios.post(url);
      return res;
    } catch (err) {
      console.error('err', err);
      FuelPortalService.showError(err);
      return null;
    }
  },

  /**
   * Call startingest with parameters
   * eg. POST https://fueltools-dev01-v1.mediapipe.com/tools/startingest?forceDownloadExternalMeta=true
   * @returns {Promise<any>}
   */
  async startIngest(payload: Item, queryParams: any = null): Promise<any> {
    let url = 'tools/startingest';
    if (queryParams) {
      url += `?${clientUtils.convertToQueryString(queryParams)}`;
    } else {
      url += `?t=${new Date().getTime()}`; // make a unique url in case api gateway is ignoring repeated startingest calls
    }
    try {
      const res = await fuelPortalAxios.post(url, payload);
      return res;
    } catch (err) {
      console.error('err', err);
      FuelPortalService.showError(err);
      return null;
    }
  },

  async batchDelete(itemIds: string[], ouId: string, entityType: string): Promise<BatchDeleteResponse | null> {
    const data = {
      ouId,
      ids: itemIds,
    };
    const url = `data/${entityType}/batchdelete`;
    const method = 'post';
    try {
      const res = await fuelPortalAxios[method](url, data);
      return res.data;
    } catch (error) {
      if (!baseAxios.isCancel(error)) {
        console.error('not successfully aborted', error);
      }
      FuelPortalService.showError(error);
      return null;
    }
  },
  /**
   * Gets clip data from FuelPortal
   * eg. https://pyw4ye3nli.execute-api.us-east-1.amazonaws.com/Prod/data/clip/dfe5e080-d159-42c7-91d1-cbc9c634f53f
   *
   * @returns {Promise<any>}
   */
  async getClipById(clipId: string): Promise<Item> {
    if (clipId == null || clipId.trim() === '') {
      throw `[getClipById] clipId cannot be empty, clipId: ${clipId}`;
    }
    const waitTime = 500 * this.getClipByIdsArray.length;
    this.getClipByIdsArray.push(clipId);
    return new Promise<any>(async (resolve, reject) => {
      setTimeout(async () => {
        try {
          const url = `data/clip/${clipId}`;
          const fullUrl = fuelPortalAxios.defaults.baseURL + url;
          const method = 'get';
          const res = await fuelPortalAxios[method](url);
          _.remove(this.getClipByIdsArray, (x) => {
            return x === clipId;
          });
          resolve(res.data);
        } catch (err) {
          // store.commit('alert/set', {
          //   message: clientUtils.errorResponseHandler(err, 'getClipById', 'Clip'),
          //   type: 'error',
          //   position: 'n',
          // });
          console.error(err);
          reject(err);
        }
      }, waitTime);
    });
  },
  /**
   * Calls the FuelPortal data/clip/{itemId}/thumbnail route to generate thumbnail request
   *
   * @returns {Promise<any>}
   */
  async GenerateThumbnailRequestForAsset(clip: Item, imageUrl = null): Promise<boolean | null> {
    const itemId = clip.id;
    const data = { ThumbnailDelay: clip.data.thumbnailDelay, ThumbnailData: null };
    const url = `data/clip/${itemId}/thumbnail`;
    if (imageUrl) {
      imageUrl = imageUrl.replace('data:image/png;base64,', '');
      data.ThumbnailData = imageUrl;
    }
    try {
      const res = await fuelPortalAxios.post(url, data);
      return res.data;
    } catch (err) {
      FuelPortalService.showError(err);
      return null;
    }
  },
  /**
   * Calls the FuelPortal data/clip/{itemId}/autitrail route to get audit trail
   *
   * @returns {Promise<any>}
   */
  async GetAssetAuditTrail(assetId: string): Promise<AssetAuditTrail[] | null> {
    const url = `data/clip/${assetId}/audittrail`;
    try {
      const res = await fuelPortalAxios.get(url);
      return res.data;
    } catch (err) {
      FuelPortalService.showError(err);
      return null;
    }
  },
  /**
   * Calls the FuelPortal tools/makecc/{clipId} route to create captions
   *
   * @returns {Promise<any>}
   */
  async createCaption(clipId: string, languageCode: string = 'en-US'): Promise<any> {
    const url = `tools/makecc/${clipId}?languageCode=${languageCode}`;
    // const fullUrl = fuelPortalAxios.defaults.baseURL + url;
    try {
      const res = await fuelPortalAxios.post(url);
      return res.data;
    } catch (err) {
      FuelPortalService.showError(err);
      return null;
    }
  },
  /**
   * Calls the FuelPortal tools/makecc/{clipId} route to create captions
   *
   * @returns {Promise<any>}
   */
  async autoFillClip(clip: Item, queryParams: any = null): Promise<any> {
    const clipId = clip.id ?? 'subclip';
    let url = `tools/autofill/clip/${clipId}`;
    if (queryParams) {
      url += `?${clientUtils.convertToQueryString(queryParams)}`;
    }
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.post(url, clip, {
        cancelToken: source.token,
      });
      return res.data;
    } catch (err) {
      FuelPortalService.showError(err);
      return null;
    }
  },
  async getClipUsers(clipId: string, showChannelUses: boolean = false): Promise<ClipUsers | null> {
    const url = `clipusers/${clipId}?showChannelUses=${showChannelUses}`;
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.get(url, {
        cancelToken: source.token,
      });
      return res.data;
    } catch (err) {
      console.error('Unable to get clip users by id ', err);
      if (!baseAxios.isCancel(err)) {
        console.error('not successfully aborted', err);
      }
      FuelPortalService.showError(err);
      return null;
    }
  },
  /**
   * Calls the FuelPortal tools/SendUiLogsToCloudWatch route to send ui logs to cloudwatch
   *
   * @returns {Promise<any>}
   */
  async sendUILogsToCloudWatch(message: any): Promise<any> {
    const fullMessage = clientUtils.formatUILogMessage(message);
    const url = `tools/SendUiLogsToCloudWatch`;
    try {
      const res = await fuelPortalAxios.post(url, fullMessage);
      return res.data;
    } catch (err) {
      throw new Error();
    }
  },
  async startSpeechToText(clipId: string, languageCode: string): Promise<any> {
    const url = languageCode ? `tools/speechtotext/${clipId}?languageCode={languageCode}` : `tools/speechtotext/${clipId}`;
    try {
      const res = await fuelPortalAxios.post(url);
      return res.data;
    } catch (err) {
      throw new Error();
    }
  },
  /**
   * Calls the FuelPortal tools/stopLive route to stop live
   *
   * @returns {Promise<any>}
   */
  async stopLiveProgram(programId: string): Promise<boolean | null> {
    const url = `tools/stopLive/${programId}`;
    try {
      const res = await fuelPortalAxios.post(url);
      return res.data;
    } catch (err) {
      FuelPortalService.showError(err);
      return null;
    }
  },
  /**
   * Calls the FuelPortal tools/streamEvents route to get recent live stream events
   * eg. tools/streamEvents?ouCode=fuel_dev_env&startDate=2024-11-30T00:00:00Z&endDate=2024-12-05T23:59:59Z
   * @returns {Promise<any>}
   */
  async getLEStreamEvents(ouCode, startDate, endDate, streamIds: string[] | null = null): Promise<any[] | null> {
    // console.log('getLEStreamEvents', { ouCode, startDate, endDate });
    const queryParam = clientUtils.convertToQueryString({ ouCode, startDate, endDate });
    let url = `tools/streamEvents?${queryParam}`;
    if (streamIds != null && streamIds.length > 0) {
      url += `&streamIds=${streamIds.join(',')}`;
    }
    try {
      const res = await fuelPortalAxios.get(url);
      return res.data;
    } catch (err) {
      console.error('getLEStreamEvents error', { err });
      return null;
    }
  },
  // new create asset from ingest controller
  async createNewAsset(asset: Item): Promise<CreateAssetResponse | null> {
    const url = `ingest/createasset`;
    try {
      const res = await fuelPortalAxios.post(url, asset);
      return res.data;
    } catch (err) {
      FuelPortalService.showError(err);
      return null;
    }
  },
  async createNewIngest(assetResult: CreateAssetResponse): Promise<CreateAssetResponse> {
    const url = `ingest/startingest`;
    try {
      // #4449 aalt 2025-09-15 remove heavy UI related properties or we may get 413 content too large error with the payload
      const copy: CreateAssetResponse = _.cloneDeep(assetResult);
      if (!clientUtils.isNullOrWhitespace(copy?.clip?.thumbnailSrc)) {
        delete copy.clip.thumbnailSrc;
      }
      if (!clientUtils.isNullOrWhitespace(copy?.clip?.data?.thumbnail?.tempThumbnailBlob)) {
        delete copy.clip.data.thumbnail.tempThumbnailBlob;
      }
      const payload = {
        createAssetResults: copy,
        ExternalSourceUrl: null,
        TranscodeVariants: [],
      };
      const res = await fuelPortalAxios.post(url, payload);
      return res.data;
    } catch (err) {
      throw new Error();
    }
  },
  /**
   * Calls the FuelPortal tools/replay route to get GetNSLSegmentsInReplay
   *
   * @returns {Promise<any>}
   */
  async getNSLSegmentsInReplay(serviceId: string): Promise<Replay | null> {
    const url = `tools/replay/${serviceId}`;
    try {
      const res = await fuelPortalAxios.get(url);
      return res.data;
    } catch (err) {
      FuelPortalService.showError(err);
      return null;
    }
  },
  /**
   * Calls the FuelPortal tools/replay route to get GetNSLSegmentsInReplay
   *
   * @returns {Promise<any>}
   */
  async deleteReplayClip(replay: Replay): Promise<boolean | null> {
    const url = `tools/replay`;
    try {
      const res = await fuelPortalAxios.delete(url, { data: replay });
      return res.data;
    } catch (err) {
      FuelPortalService.showError(err);
      return null;
    }
  },
  /**
   * Calls the FuelPortal `tools/releaseLock/${entityType}/${id}?ouId=${ouId}` route to releaselock
   *
   * @returns {Promise<any>}
   */
  async releaseLock(entityType: FuelEntityType, id: string, ouId: string): Promise<any> {
    // Build full absolute URL
    const baseUrl = fuelPortalAxios.defaults.baseURL; //
    let url = `tools/releaseLock/${entityType}/${id}?ouId=${ouId}`;

    const isUnload = typeof navigator !== 'undefined' && navigator.sendBeacon;
    const token = LocalStorageService.getItem('ACCESS_TOKEN');
    if (isUnload && token) {
      // Use fetch + keepalive for unload / refresh
      try {
        url = `${baseUrl}tools/releaseLock/${entityType}/${id}?ouId=${ouId}`;
        await fetch(url, {
          method: 'GET', // or POST if your endpoint supports
          headers: {
            Authorization: `Bearer ${token}`,
          },
          keepalive: true, // critical for page unload
        });
        return true;
      } catch (err) {
        console.error('Failed to release lock on unload', err);
        return null;
      }
    } else {
      // Normal axios request
      try {
        const res = await fuelPortalAxios.get(url, { timeout: 30000 });
        return res.data;
      } catch (err) {
        FuelPortalService.showError(err);
        return null;
      }
    }
  },

  /**
   * Calls the FuelPortal `tools/checkLock/${entityType}/${id}?ouId=${ouId}` route to checkLock
   *
   * @returns {Promise<any>}
   */
  async getLock(entityType: FuelEntityType, id: string, metaData: any = null): Promise<any> {
    const url = `tools/checkLock/${entityType}/${id}`;
    // const fullUrl = fuelPortalAxios.defaults.baseURL + url;
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.post(url, metaData);
      return res.data;
    } catch (err) {
      FuelPortalService.showError(err);
      return null;
    }
  },
  /**
   * Calls the FuelPortal `data/checkalias` route to checkAlias
   *
   * @returns {Promise<any>}
   */
  async checkAlias(payload: CheckEnitityAliasRequest): Promise<AxiosResponse> {
    const url = `data/checkAlias`;
    // const fullUrl = fuelPortalAxios.defaults.baseURL + url;
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.post(url, payload);
      return res;
    } catch (err) {
      // FuelPortalService.showError(err);
      return Promise.reject(err);
    }
  },
  async resetSession(sessionId: string, ouId: string): Promise<AxiosResponse> {
    const url = `tools/session-reset/${sessionId}?ouId=${ouId}`;
    // const fullUrl = fuelPortalAxios.defaults.baseURL + url;
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.delete(url);
      return res;
    } catch (err) {
      // FuelPortalService.showError(err);
      return Promise.reject(err);
    }
  },
  async validateSiteCode(siteCode: string): Promise<AxiosResponse> {
    const url = `Sites?key=${siteCode}`;
    // const fullUrl = fuelPortalAxios.defaults.baseURL + url;
    try {
      const source = baseAxios.CancelToken.source();
      const res = await fuelPortalAxios.get(url);
      return res;
    } catch (err) {
      // FuelPortalService.showError(err);
      return Promise.reject(err);
    }
  },
};

export { FuelEntityType, FuelPortalService };
